import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import pluralize from 'pluralize';
import {
  getFormattedDateFromIsoDate,
  classNames,
  getFolderUrl,
  iconTypesForLatestUploads,
} from '../../utils/v4/utils';
import AssetImage from '../Assets/AssetImage';
import { capitalize } from '../../containers/Pages/Edd/EddUtils';
import urlHelper from '../../utils/urlHelper';

/**
 * @typedef {Object} ContentTableItem
 * @property {string|number} id - Unique table row identifier.
 * @property {string|number} [content_id] - Back-end content identifier.
 * @property {'folder'|'image'|'video'|'file'|'playlist'|'sign'|'sequence'|'schedule'|'campaign'|string} type - Content type used for navigation and actions.
 * @property {string} name - Display name shown in the table.
 * @property {string} [createdDate] - ISO timestamp used for the created date column.
 * @property {string} [parent] - Parent folder label or path.
 * @property {string[]} [tags] - Tags associated with the content item.
 */

/**
 * @typedef {Object.<number, ContentTableItem>} SelectedContentMap
 */

/**
 * @typedef {Object} ContentTableProps
 * @property {ContentTableItem[]} itemsToDisplay - Items rendered as rows in the table.
 * @property {(item: ContentTableItem, isFolderEdit?: boolean) => void} onLatestUploadItemClick - Opens the selected content item or folder editor.
 * @property {(options: { title: string, subtitle: string, content: ContentTableItem }) => void} prepareDeleteFunction - Opens the delete confirmation flow for an item.
 * @property {SelectedContentMap} selectedContent - Map of selected row indexes to selected content items.
 * @property {(event: MouseEvent, content: ContentTableItem, index: number) => void} handleContentSelectedChange - Updates the row selection state.
 * @property {Array<object>} [signs] - Sign data used by the asset preview component.
 * @property {string|string[]} [itemType] - Active filter type used to build the table heading.
 * @property {{ push: (url: string) => void }} history - Router history used for navigation.
 * @property {object} [auth] - Auth slice injected by Redux.
 */

/**
 * Renders the content table used on the main content page.
 * @param {ContentTableProps} props - Data and callbacks required to render and interact with the table.
 * @returns {JSX.Element} The rendered content table section.
 */
export function ContentTableWithoutRedux(props) {
  const {
    itemsToDisplay = [],
    onLatestUploadItemClick,
    prepareDeleteFunction,
    selectedContent,
    handleContentSelectedChange,
    signs,
    itemType = '',
  } = props;

  /**
   * Navigates to the content detail page or folder view when a row is double-clicked.
   * @param {MouseEvent} event - The browser double-click event for the row.
   * @param {ContentTableItem} item - The item associated with the clicked row.
   * @returns {void}
   */
  const onDoubleClick = (event, item) => {
    const { history } = props;
    const url = item.type === 'folder' ? getFolderUrl(item, props) : urlHelper.getContentUrl(item);

    history.push(url);
  };

  return (
    <div className="mt-4">
      <div className="bg-white mb-0 mt-0 w-full border-t-2 border-l-2 border-r-2 rounded-t-[12px]">
        <p
          data-test="latest-uploads-text"
          className="poppins-bold font-700 text-[16px] mt-2 ml-[1.5%] mb-0 pb-2 pt-2 pl-1 text-dark-night"
        >
          Latest {pluralize(capitalize(typeof itemType === 'string' ? itemType : 'file'))}
        </p>
      </div>

      <div className="md:w-full h-[570px] w-full border-b-2 border-l-2 border-r-2 bg-white overflow-y-auto rounded-b-[12px]">
        <table data-test="contents-table" className="w-[97%] mx-auto border-collapse table-fixed">
          <colgroup>
            <col className="w-[120px]" />
            <col className="" />
            <col className="w-0 md:w-[50px]" />
            <col className="w-0 md:w-auto" />
            <col className="w-[100px]" />
          </colgroup>
          <tbody className="border-t-2 border-gray-200">
            {itemsToDisplay.map((content, index) => (
              <tr
                data-test={`contents-table-row-${content.id}`}
                key={content.id}
                className={classNames(
                  'h-[60px] border-b-2 border-gray-200 cursor-pointer',
                  selectedContent[index] === undefined ? '' : 'bg-[#ff5f0036]',
                )}
                onClick={e => handleContentSelectedChange(e, content, index)}
                onDoubleClick={e => onDoubleClick(e, content)}
              >
                {/* Icon Column */}
                <td className="w-[120px] align-middle">
                  <div className=" w-[120px] flex items-center justify-center">
                    <AssetImage
                      id={content.content_id}
                      type={content.type}
                      item={content}
                      signs={signs}
                    />
                  </div>
                </td>

                <td className=" align-middle">
                  <div className="w-full">
                    <p
                      onClick={e => {
                        e.stopPropagation();
                        onLatestUploadItemClick(content);
                      }}
                      className="text-[#00243E] truncate  max-w-full inline-block hover:text-primary transition-all duration-200 font-[700] poppins-extrabold text-[12px] mb-0"
                      data-test="content-name"
                    >
                      {content.name}
                    </p>
                    <p
                      className="text-[#00243E] truncate max-w-full font-[400] poppins-regular text-[12px] mb-0"
                      data-test="content-created-date"
                    >
                      {getFormattedDateFromIsoDate(content.createdDate)}
                    </p>
                  </div>
                </td>

                {/* Type Icon Column */}
                <td className="overflow-hidden align-middle">
                  <div className="flex items-center justify-center">
                    {iconTypesForLatestUploads[content.type] && (
                      <img
                        data-test={`content-type-icon-${content.id}`}
                        src={iconTypesForLatestUploads[content.type]}
                        alt={`${content.type} icon`}
                        className="w-[28px] h-[28px]"
                        draggable={false}
                      />
                    )}
                  </div>
                </td>

                <td className="overflow-hidden align-middle">
                  <div className="w-full">
                    <p
                      className="text-[#2c6893] truncate max-w-full font-[400] poppins-regular text-[12px] mb-0"
                      data-test="content-parent"
                    >
                      {content.parent}
                    </p>
                    {content.tags && content.tags.some(t => t.length > 0) && (
                      <div data-test="content-tags" className="flex flex-wrap gap-1">
                        {content.tags
                          .filter(t => t.length > 0)
                          .map(tag => (
                            <span
                              key={tag}
                              className="inline-flex items-center rounded-md bg-orange-50 px-2 py-1 text-xs font-medium text-sunrise inset-ring inset-ring-orange-600/20"
                            >
                              {tag}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                </td>

                <td className="align-middle w-[100px]">
                  <div className=" w-full flex space-x-2 justify-end">
                    <button
                      className="w-[34px] h-[34px] cursor-pointer duration-500 hover:scale-105"
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        onLatestUploadItemClick(content, content.type === 'folder');
                      }}
                      data-test="content-edit-button"
                    >
                      <img src="/assets/images/icons/edit.svg" alt="edit icon" draggable={false} />
                    </button>

                    <button
                      type="button"
                      className="w-[34px] h-[34px] cursor-pointer duration-500 hover:scale-105"
                      onClick={e => {
                        e.stopPropagation();
                        prepareDeleteFunction({
                          title: 'Delete Item',
                          subtitle: 'Are you sure you want to delete this item? ',
                          content,
                        });
                      }}
                      data-test="content-delete-button"
                    >
                      <img
                        src="/assets/images/icons/remove_file.svg"
                        alt="remove file icon"
                        draggable={false}
                      />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="h-[2px] w-[97%] mx-auto" />
      </div>
    </div>
  );
}

ContentTableWithoutRedux.propTypes = {
  itemsToDisplay: PropTypes.array.isRequired,
  onLatestUploadItemClick: PropTypes.func.isRequired,
  prepareDeleteFunction: PropTypes.func.isRequired,
  handleContentSelectedChange: PropTypes.func.isRequired,
  selectedContent: PropTypes.object.isRequired,
  signs: PropTypes.array,
  itemType: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  history: PropTypes.object.isRequired,
};

/**
 * Maps the Redux auth state into the connected component props.
 * @param {{ auth: object }} state - Redux state containing the auth slice.
 * @returns {{ auth: object }} Connected props derived from Redux state.
 */
const mapStateToProps = state => ({
  auth: state.auth,
});

export default withRouter(connect(mapStateToProps)(ContentTableWithoutRedux));
